import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { AnalyticsCharts } from "./AnalyticsCharts";
import { AppDownloads } from "@/components/dashboard/AppDownloads";

const Stat = ({ label, value, tone = "", testId }: { label: string; value: any; tone?: string; testId: string }) => (
  <div className={`min-w-0 rounded-2xl border border-brand-line bg-white p-4 shadow-subtle sm:p-5 ${tone}`}>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-slate sm:text-xs sm:tracking-widest">{label}</p>
    <p className="mt-2 break-words font-heading text-lg font-bold leading-tight text-brand-navy sm:text-2xl" data-testid={testId}>{value}</p>
  </div>
);

export default function Overview() {
  const [s, setS] = useState<any>(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-sm text-brand-slate">Loading…</p>;
  const gmv = Object.entries(s.gmv_minor || {}).map(([c, v]) => money(v as number, c)).join(" + ") || "£0.00";
  return (
    <div data-testid="admin-overview">
      <PageHeader eyebrow="Operations" title="Overview" desc="Live snapshot of the UK/US → Zimbabwe corridor." />
      <div className="-mt-4 mb-8"><AppDownloads /></div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Stat label="GMV (escrow in)" value={gmv} tone="border-brand-gold/40 shadow-gold" testId="stat-gmv" />
        <Stat label="Bookings" value={s.bookings_total} testId="stat-bookings" />
        <Stat label="Escrow held" value={s.bookings_escrow_held} testId="stat-escrow-held" />
        <Stat label="Unassigned bookings" value={s.bookings_unassigned} testId="stat-unassigned" />
        <Stat label="Verified caregivers" value={`${s.caregivers_verified} (${s.employed_count} employed · ${s.self_employed_count} contractors)`} testId="stat-caregivers" />
        <Stat label="Verification queue" value={s.caregivers_pending} testId="stat-pending" />
        <Stat label="Employed utilization" value={`${s.employed_utilization_pct}%`} testId="stat-utilization" />
        <Stat label="Dispute rate" value={`${s.dispute_rate_pct}%`} testId="stat-dispute-rate" />
        <Stat label="Waitlist signups" value={s.waitlist_count} testId="stat-waitlist" />
        <Stat label="Diaspora accounts" value={s.users_count} testId="stat-users" />
        <Stat label="WhatsApp messages" value={s.whatsapp_messages} testId="stat-whatsapp" />
        <Stat label="Payouts queued" value={s.pending_payouts} testId="stat-payouts" />
      </div>
      <AnalyticsCharts />
      <div className="mt-8 rounded-2xl border border-dashed border-brand-line bg-white/60 p-6 text-sm text-brand-slate" data-testid="phase-notice">
        <p className="font-semibold text-brand-navy">Phase 1 status</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Stripe: claimable sandbox, test mode, GBP/USD catalog synced.</li>
          <li>WhatsApp: Twilio webhook ready at <code>/api/whatsapp/webhook</code>; outbound messages are <strong>MOCKED</strong> (logged) until Meta approval.</li>
          <li>Payouts (Pesepay/Paynow): weekly run live every Friday; provider call <strong>MOCKED</strong> until merchant keys are set (PAYOUT_PROVIDER in .env).</li>
        </ul>
      </div>
    </div>
  );
}
